import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { PRODUCT_TYPES } from '../../constants';
import { formatPrice, parseTulExtraPricesFromSettings, parseKanatExtraPricesFromSettings, buildTulPileSettingsPayload, buildKanatPileSettingsPayload, parseStorExtrasFromSettings, parseZebraExtrasFromSettings, buildStorSettingsPayload, buildZebraSettingsPayload, parsePliseExtrasFromSettings, buildPliseSettingsPayload, parseKatlamaliExtrasFromSettings, buildKatlamaliSettingsPayload } from '../../utils';
import * as XLSX from 'xlsx';
import ExchangeRateSettings from './subcomponents/ExchangeRateSettings';
import ProductCategoryNav from './subcomponents/ProductCategoryNav';
import ProductTableHeader from './subcomponents/ProductTableHeader';
import ProductSearchBar from './subcomponents/ProductSearchBar';
import ProductDataTable from './subcomponents/ProductDataTable';
import ProductTypeSettings from './subcomponents/ProductTypeSettings';
import ProductFormModal from './subcomponents/modals/ProductFormModal';
import DeleteConfirmModal from './subcomponents/modals/DeleteConfirmModal';
import ExcelStatusModal from './subcomponents/modals/ExcelStatusModal';
import './ProductList.css';

const ProductList = () => {
  const { permissions } = useAuth();
  const canEditProducts = permissions?.canEditProducts;

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [activeTypeTab, setActiveTypeTab] = useState(Object.keys(PRODUCT_TYPES)[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const fileInputRef = useRef(null);

  const [tulExtraPrices, setTulExtraPrices] = useState({
    sPile: '', american2: '', american3: '',
    kapsul: '', altEtekKatlama: ''
  });
  const [kanatExtraPrices, setKanatExtraPrices] = useState({
    sPile: '', american2: '', american3: '',
    kapsul: ''
  });
  const [useManualRates, setUseManualRates] = useState(false);
  const [manualUsdRate, setManualUsdRate] = useState('');
  const [manualEurRate, setManualEurRate] = useState('');
  const [jaluziExtras, setJaluziExtras] = useState({ zigil: '', reduktor: '', kurdela: '', motorCurrency: 'USD', motorPrice: '' });
  const [metalJaluziExtras, setMetalJaluziExtras] = useState({ zigil: '', reduktor: '', ikiCamArasi: '', motorCurrency: 'USD', motorPrice: '' });
  const [exchangeRates, setExchangeRates] = useState({ USD: 1, EUR: 1 });
  const [storExtras, setStorExtras] = useState({
    jumbo: '',
    altEtekDilim: '',
    kapaliKasa: '',
    altEtekLazer: '',
    dekoratifLama: '',
    dekoratifKasa: '',
    altEtekBeyazIs: '',
    boru47: '',
    mekanizma47: '',
    motorCurrency: 'USD', motorPrice: ''
  });
  const [zebraExtras, setZebraExtras] = useState({
    jumbo: '',
    altEtekDilim: '',
    kapaliKasa: '',
    motorCurrency: 'USD', motorPrice: ''
  });
  const [dikeyExtras, setDikeyExtras] = useState({ fon: '', fonList: [], motorCurrency: 'USD', motorPrice: '' });
  const [dikeyPvcExtras, setDikeyPvcExtras] = useState({ motorCurrency: 'USD', motorPrice: '' });
  const [dikeyStorExtras, setDikeyStorExtras] = useState({ motorCurrency: 'USD', motorPrice: '' });
  const [katlamaliExtras, setKatlamaliExtras] = useState({ mekanizmaList: [], motorCurrency: 'USD', motorPrice: '', dikimUcreti: '' });
  const [pliseExtras, setPliseExtras] = useState({
    portrayliMontaj: '',
    yapistirmaSistem: '',
    isicamliYapistirma: ''
  });
  const [newDikeyFonName, setNewDikeyFonName] = useState('');
  const [newKatlamaliMekanizma, setNewKatlamaliMekanizma] = useState({ type: '', price: '' });
  const [isFonListCollapsed, setIsFonListCollapsed] = useState(true);
  const [editingDikeyFonName, setEditingDikeyFonName] = useState({ index: null, value: '' });
  const [isKatlamaliMekanizmaCollapsed, setIsKatlamaliMekanizmaCollapsed] = useState(true);
  const [rustikExtras, setRustikExtras] = useState({
    ayakModelList: [],
    ayakCapList: [],
    ayakUzunlukList: [],
    ayakRenkKoduList: [],
    ayakPrice: ''
  });
  const [newRustikModel, setNewRustikModel] = useState('');
  const [newRustikCap, setNewRustikCap] = useState('');
  const [newRustikUzunluk, setNewRustikUzunluk] = useState('');
  const [newRustikRenk, setNewRustikRenk] = useState('');
  const [isRustikSettingsCollapsed, setIsRustikSettingsCollapsed] = useState(true);
  const [isRustikModelCollapsed, setIsRustikModelCollapsed] = useState(true);
  const [isRustikCapCollapsed, setIsRustikCapCollapsed] = useState(true);
  const [isRustikUzunlukCollapsed, setIsRustikUzunlukCollapsed] = useState(true);
  const [isRustikRenkCollapsed, setIsRustikRenkCollapsed] = useState(true);

  const [editingRustikModel, setEditingRustikModel] = useState({ index: null, value: '' });
  const [editingRustikCap, setEditingRustikCap] = useState({ index: null, value: '' });
  const [editingRustikUzunluk, setEditingRustikUzunluk] = useState({ index: null, value: '' });
  const [editingRustikRenk, setEditingRustikRenk] = useState({ index: null, value: '' });

  useEffect(() => {
    // Fetch Settings
    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'global');
        const docSnap = await getDoc(settingsRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setTulExtraPrices(parseTulExtraPricesFromSettings(data));
          setKanatExtraPrices(parseKanatExtraPricesFromSettings(data));
          setUseManualRates(data.useManualRates || false);
          setManualUsdRate(data.manualUsdRate || '');
          setManualEurRate(data.manualEurRate || '');
          setJaluziExtras({
            zigil: data.zigilPrice || '',
            motorCurrency: data.ahsapJaluziMotorCurrency || 'USD', motorPrice: data.ahsapJaluziMotorPrice || ''
          });
          setMetalJaluziExtras({
            zigil: data.metalZigilPrice || '',
            reduktor: data.metalReduktorPrice || '',
            ikiCamArasi: data.ikiCamArasiPrice || '',
            motorCurrency: data.metalJaluziMotorCurrency || 'USD', motorPrice: data.metalJaluziMotorPrice || ''
          });
          setRustikExtras({
            ayakModelList: data.rustikAyakModelList || [],
            ayakCapList: data.rustikAyakCapList || [],
            ayakUzunlukList: data.rustikAyakUzunlukList || [],
            ayakRenkKoduList: data.rustikAyakRenkKoduList || [],
            ayakPrice: data.rustikAyakPrice || ''
          });
          setStorExtras(parseStorExtrasFromSettings(data));
          setZebraExtras(parseZebraExtrasFromSettings(data));
          setDikeyExtras({
            fon: data.dikeyFonPrice || '',
            fonList: data.dikeyFonList || [],
            motorCurrency: data.dikeyPerdeMotorCurrency || 'USD', motorPrice: data.dikeyPerdeMotorPrice || ''
          });
          setDikeyPvcExtras({
            motorCurrency: data.dikeyPvcMotorCurrency || 'USD', motorPrice: data.dikeyPvcMotorPrice || ''
          });
          setDikeyStorExtras({
            motorCurrency: data.dikeyStorMotorCurrency || 'USD', motorPrice: data.dikeyStorMotorPrice || ''
          });
          setKatlamaliExtras(parseKatlamaliExtrasFromSettings(data));
          setPliseExtras(parsePliseExtrasFromSettings(data));
        }
      } catch (err) {
        console.error('Settings fetch error:', err);
      }
    };
    fetchSettings();

    // Fetch Exchange Rates
    const fetchRates = async () => {
      try {
        const res = await fetch('https://open.er-api.com/v6/latest/USD');
        const data = await res.json();
        if (data && data.rates && data.rates.TRY && data.rates.EUR) {
          const usdToTry = data.rates.TRY;
          const eurToTry = data.rates.TRY / data.rates.EUR;
          setExchangeRates({ USD: usdToTry.toFixed(2), EUR: eurToTry.toFixed(2) });
        }
      } catch (err) {
        console.error('Exchange rates error:', err);
      }
    };
    fetchRates();
  }, []);

  const handleSaveSettings = async (overrideData = null) => {
    setIsSavingSettings(true);
    try {
      const settingsRef = doc(db, 'settings', 'global');
      let updateData = overrideData || {};

      if (!overrideData) {
        if (activeTypeTab === 'tul') {
          updateData = buildTulPileSettingsPayload(tulExtraPrices);
        } else if (activeTypeTab === 'kanat') {
          updateData = buildKanatPileSettingsPayload(kanatExtraPrices);
        } else if (activeTypeTab === 'ahsapJaluzi') {
          updateData = {
            ahsapJaluziMotorCurrency: jaluziExtras.motorCurrency,
            ahsapJaluziMotorPrice: Number(jaluziExtras.motorPrice) || '',
            zigilPrice: Number(jaluziExtras.zigil)
          };
        } else if (activeTypeTab === 'metalJaluzi') {
          updateData = {
            metalJaluziMotorCurrency: metalJaluziExtras.motorCurrency,
            metalJaluziMotorPrice: Number(metalJaluziExtras.motorPrice) || '',
            metalZigilPrice: Number(metalJaluziExtras.zigil),
            metalReduktorPrice: Number(metalJaluziExtras.reduktor),
            ikiCamArasiPrice: Number(metalJaluziExtras.ikiCamArasi)
          };
        } else if (activeTypeTab === 'stor') {
          updateData = buildStorSettingsPayload(storExtras);
        } else if (activeTypeTab === 'zebra') {
          updateData = buildZebraSettingsPayload(zebraExtras);
        } else if (activeTypeTab === 'dikeyPerde') {
          updateData = {
            dikeyFonPrice: Number(dikeyExtras.fon),
            dikeyFonList: dikeyExtras.fonList || [],
            dikeyPerdeMotorCurrency: dikeyExtras.motorCurrency,
            dikeyPerdeMotorPrice: Number(dikeyExtras.motorPrice) || ''
          };
        } else if (activeTypeTab === 'dikeyPvc') {
          updateData = {
            dikeyPvcMotorCurrency: dikeyPvcExtras.motorCurrency,
            dikeyPvcMotorPrice: Number(dikeyPvcExtras.motorPrice) || ''
          };
        } else if (activeTypeTab === 'dikeyStor') {
          updateData = {
            dikeyStorMotorCurrency: dikeyStorExtras.motorCurrency,
            dikeyStorMotorPrice: Number(dikeyStorExtras.motorPrice) || ''
          };
        } else if (activeTypeTab === 'katlamaliPerde') {
          updateData = buildKatlamaliSettingsPayload(katlamaliExtras);
        } else if (activeTypeTab === 'rustik') {
          updateData = {
            rustikAyakModelList: rustikExtras.ayakModelList || [],
            rustikAyakCapList: rustikExtras.ayakCapList || [],
            rustikAyakUzunlukList: rustikExtras.ayakUzunlukList || [],
            rustikAyakRenkKoduList: rustikExtras.ayakRenkKoduList || [],
            rustikAyakPrice: rustikExtras.ayakPrice || ''
          };
        } else if (activeTypeTab === 'plise') {
          updateData = buildPliseSettingsPayload(pliseExtras);
        }
      }

      await setDoc(settingsRef, updateData, { merge: true });

      // Manuel kur verileri geldiyse ana state'i güncelle
      if (overrideData) {
        if (overrideData.hasOwnProperty('useManualRates')) setUseManualRates(overrideData.useManualRates);
        if (overrideData.hasOwnProperty('manualUsdRate')) setManualUsdRate(overrideData.manualUsdRate);
        if (overrideData.hasOwnProperty('manualEurRate')) setManualEurRate(overrideData.manualEurRate);
      }

      if (!overrideData || activeTypeTab === 'rustik') {
        setExcelModal({ show: true, type: 'success', title: 'Başarılı', message: 'Ayarlar başarıyla kaydedildi.' });
      }
    } catch (err) {
      console.error('Settings save error:', err);
      if (!overrideData) {
        setExcelModal({ show: true, type: 'error', title: 'Hata', message: 'Ayarlar kaydedilirken bir hata oluştu.' });
      }
    }
    setIsSavingSettings(false);
  };

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    productType: Object.keys(PRODUCT_TYPES)[0],
    unitPrice: '',
    maxWidth200cm: false,
    // Jaluziler için ekstra fiyatlandırma
    merdivenIpliFiyat: '',
    kurdeleliFiyat: '',
    merdivenIpliReduktorluFiyat: '',
    kurdelaliReduktorluFiyat: ''
  });
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Silme modal
  const [deleteModal, setDeleteModal] = useState(null);
  const [excelModal, setExcelModal] = useState({ show: false, type: '', title: '', message: '', onConfirm: null });

  // Ürünleri Firestore'dan çek
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'productItems'));
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      // İsme göre sırala
      data.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setProducts(data);
    } catch (error) {
      console.error('Ürünler yüklenirken hata:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Sekme değişince arama kutusunu sıfırla
  const handleTabChange = (key) => {
    setActiveTypeTab(key);
    setSearchQuery('');
  };

  // Aktif sekmedeki ürünler (arama filtreli) - useMemo ile performans optimizasyonu
  const filteredProducts = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return products.filter(p => {
      if (p.productType !== activeTypeTab) return false;
      if (!query) return true;
      return (p.name || '').toLowerCase().includes(query);
    });
  }, [products, activeTypeTab, searchQuery]);

  // Form aç - yeni ürün
  const handleAddNew = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      productType: activeTypeTab,
      unitPrice: '',
      maxWidth200cm: false,
      merdivenIpliFiyat: '',
      kurdeleliFiyat: '',
      merdivenIpliReduktorluFiyat: '',
      kurdelaliReduktorluFiyat: ''
    });
    setFormError(null);
    setFormSuccess(null);
    setShowForm(true);
  };

  // Form aç - düzenle
  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      productType: product.productType || activeTypeTab,
      unitPrice: product.unitPrice || '',
      maxWidth200cm: !!product.maxWidth200cm,
      merdivenIpliFiyat: product.merdivenIpliFiyat || '',
      kurdeleliFiyat: product.kurdeleliFiyat || '',
      merdivenIpliReduktorluFiyat: product.merdivenIpliReduktorluFiyat || '',
      kurdelaliReduktorluFiyat: product.kurdelaliReduktorluFiyat || ''
    });
    setFormError(null);
    setFormSuccess(null);
    setShowForm(true);
  };

  // Form kapat
  const handleCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormError(null);
  };

  // Kaydet
  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    if (!formData.name.trim()) {
      setFormError('Ürün adı zorunludur.');
      return;
    }

    const isJaluzi = formData.productType === 'ahsapJaluzi';
    const isRustik = formData.productType === 'rustik';

    if (!isJaluzi && !isRustik && (!formData.unitPrice || isNaN(parseFloat(formData.unitPrice)))) {
      setFormError('Birim fiyat geçerli bir sayı olmalıdır.');
      return;
    }

    // Jaluziler için en az bir baz fiyat gerekli mi diye kontrol edilebilir, opsiyonel bırakıyoruz/esnek.

    setIsSaving(true);
    try {
      let payload = {
        name: formData.name.trim(),
        productType: formData.productType,
        updatedAt: new Date()
      };

      if (isJaluzi) {
        payload = {
          ...payload,
          merdivenIpliFiyat: formData.merdivenIpliFiyat ? parseFloat(formData.merdivenIpliFiyat) : 0,
          kurdeleliFiyat: formData.kurdeleliFiyat ? parseFloat(formData.kurdeleliFiyat) : 0,
          merdivenIpliReduktorluFiyat: formData.merdivenIpliReduktorluFiyat ? parseFloat(formData.merdivenIpliReduktorluFiyat) : 0,
          kurdelaliReduktorluFiyat: formData.kurdelaliReduktorluFiyat ? parseFloat(formData.kurdelaliReduktorluFiyat) : 0
        };
      } else if (isRustik) {
        payload = {
          ...payload,
          unitPrice: 0
        };
      } else {
        payload = {
          ...payload,
          unitPrice: parseFloat(formData.unitPrice)
        };
        if (formData.productType === 'stor') {
          payload.maxWidth200cm = !!formData.maxWidth200cm;
        }
      }

      if (editingProduct) {
        await updateDoc(doc(db, 'productItems', editingProduct.id), payload);
        setFormSuccess('Ürün başarıyla güncellendi!');
      } else {
        await addDoc(collection(db, 'productItems'), {
          ...payload,
          createdAt: new Date()
        });
        setFormSuccess('Ürün başarıyla eklendi!');
      }

      await fetchProducts();
      // Yeni ürün eklenince o tipin sekmesine geç
      setActiveTypeTab(formData.productType);

      setTimeout(() => {
        setShowForm(false);
        setEditingProduct(null);
        setFormSuccess(null);
      }, 1200);
    } catch (error) {
      console.error('Ürün kaydedilirken hata:', error);
      setFormError('Ürün kaydedilirken bir hata oluştu: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Silme
  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await deleteDoc(doc(db, 'productItems', deleteModal.id));
      setDeleteModal(null);
      await fetchProducts();
    } catch (error) {
      console.error('Ürün silinirken hata:', error);
    }
  };

  // Excel'e Aktar
  const STOR_EN_TAVAN_COLUMN = 'En Tavanı';

  const parseStorMaxWidth200cm = (value) => {
    const normalized = String(value ?? '').toLowerCase().trim();
    if (['evet', '1', 'true', '200 cm', '200cm', '200'].includes(normalized)) return true;
    return false;
  };

  const handleExportExcel = () => {
    if (filteredProducts.length === 0) {
      setExcelModal({ show: true, type: 'info', title: 'Bilgi', message: 'Dışa aktarılacak ürün bulunamadı.' });
      return;
    }

    const isJaluzi = activeTypeTab === 'ahsapJaluzi';

    const exportData = filteredProducts.map(p => {
      if (isJaluzi) {
        return {
          'Ürün Adı': p.name,
          'Merdiven İpli': p.merdivenIpliFiyat || 0,
          'Kurdeleli': p.kurdeleliFiyat || 0,
          'Merdiven İpli Redüktörlü': p.merdivenIpliReduktorluFiyat || 0,
          'Kurdelalı Redüktörlü': p.kurdelaliReduktorluFiyat || 0
        };
      }
      if (activeTypeTab === 'stor') {
        return {
          'Ürün Adı': p.name,
          'Birim Fiyat': p.unitPrice,
          [STOR_EN_TAVAN_COLUMN]: p.maxWidth200cm ? '200 cm' : 'Serbest'
        };
      }
      return {
        'Ürün Adı': p.name,
        'Birim Fiyat': p.unitPrice
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ürünler');

    const typeLabel = PRODUCT_TYPES[activeTypeTab]?.label || 'Urunler';
    XLSX.writeFile(workbook, `${typeLabel}_Urun_Listesi.xlsx`);
  };

  // Excel'den Yükle butonu tıklaması
  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Listeyi Temizle
  const handleClearList = () => {
    const typeLabel = PRODUCT_TYPES[activeTypeTab]?.label;
    const currentCount = products.filter(p => p.productType === activeTypeTab).length;
    if (currentCount === 0) {
      setExcelModal({ show: true, type: 'info', title: 'Bilgi', message: 'Bu kategoride silinecek ürün bulunamadı.' });
      return;
    }
    setExcelModal({
      show: true,
      type: 'confirm',
      title: 'Listeyi Temizle',
      message: `DİKKAT! "${typeLabel}" kategorisindeki ${currentCount} ürünün TAMAMI silinecektir.\n\nBu işlem geri alınamaz. Emin misiniz?`,
      onConfirm: async () => {
        setExcelModal({ show: false });
        setIsExcelLoading(true);
        try {
          const toDelete = products.filter(p => p.productType === activeTypeTab);

          // Chunked batch deletion (Firebase 500 limit koruması)
          const CHUNK_SIZE = 400;
          for (let i = 0; i < toDelete.length; i += CHUNK_SIZE) {
            const chunk = toDelete.slice(i, i + CHUNK_SIZE);
            const batch = writeBatch(db);
            chunk.forEach(p => batch.delete(doc(db, 'productItems', p.id)));
            await batch.commit();
          }

          setExcelModal({ show: true, type: 'success', title: 'Temizlendi', message: `${toDelete.length} ürün başarıyla silindi.` });
          await fetchProducts();
        } catch (err) {
          setExcelModal({ show: true, type: 'error', title: 'Hata', message: 'Liste temizlenirken bir hata oluştu: ' + err.message });
        } finally {
          setIsExcelLoading(false);
        }
      }
    });
  };

  // Excel'den Dataları Veritabanına Yükle — Upsert mantığı (ada göre eşleştir)
  const processExcelData = async (data, typeLabel, importOptions = {}) => {
    const { hasStorEnTavanColumn = false } = importOptions;
    setExcelModal({ show: false });
    try {
      const isJaluzi = activeTypeTab === 'ahsapJaluzi';

      // Mevcut ürünleri normalize edilmiş ada göre map'le
      const existingByName = {};
      products
        .filter(p => p.productType === activeTypeTab)
        .forEach(p => {
          existingByName[(p.name || '').toLowerCase().trim()] = p;
        });

      const CHUNK_SIZE = 400;
      let operations = [];
      let updateCount = 0;
      let addCount = 0;

      for (const row of data) {
        const name = row['Ürün Adı']?.toString().trim();
        if (!name) continue;

        let payload = {
          name,
          productType: activeTypeTab,
          updatedAt: new Date()
        };

        let isValid = false;

        if (isJaluzi) {
          payload = {
            ...payload,
            merdivenIpliFiyat: parseFloat(row['Merdiven İpli']) || 0,
            kurdeleliFiyat: parseFloat(row['Kurdeleli']) || 0,
            merdivenIpliReduktorluFiyat: parseFloat(row['Merdiven İpli Redüktörlü']) || 0,
            kurdelaliReduktorluFiyat: parseFloat(row['Kurdelalı Redüktörlü']) || 0
          };
          isValid = true;
        } else {
          const unitPrice = parseFloat(row['Birim Fiyat']);
          if (!isNaN(unitPrice)) {
            payload = { ...payload, unitPrice };
            isValid = true;
          }
          if (activeTypeTab === 'stor' && hasStorEnTavanColumn) {
            payload.maxWidth200cm = parseStorMaxWidth200cm(row[STOR_EN_TAVAN_COLUMN]);
          }
        }

        if (!isValid) continue;

        const existing = existingByName[name.toLowerCase()];
        if (existing) {
          // Mevcut ürünü güncelle
          operations.push((b) => b.update(doc(db, 'productItems', existing.id), payload));
          updateCount++;
        } else {
          // Yeni ürün ekle
          const newRef = doc(collection(db, 'productItems'));
          const createdAt = new Date();
          operations.push((b) => b.set(newRef, { ...payload, createdAt }));
          addCount++;
        }
      }

      // Chunklar halinde commit et
      for (let i = 0; i < operations.length; i += CHUNK_SIZE) {
        const chunk = operations.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);
        for (const op of chunk) {
          op(batch);
        }
        await batch.commit();
      }

      const msg = [
        updateCount > 0 ? `${updateCount} ürün güncellendi` : null,
        addCount > 0 ? `${addCount} yeni ürün eklendi` : null
      ].filter(Boolean).join(', ');

      setExcelModal({ show: true, type: 'success', title: 'Başarılı', message: msg ? `${msg}.` : 'Değişiklik yapılmadı.' });
      await fetchProducts();
    } catch (error) {
      console.error('Excel içe aktarılırken hata:', error);
      setExcelModal({ show: true, type: 'error', title: 'Hata', message: 'Excel dosyası okunurken bir hata oluştu: ' + error.message });
    } finally {
      setIsExcelLoading(false);
    }
  };

  // Excel Dosya Seçimi ve İşlenmesi
  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // input değerini sıfırlayalım ki aynı dosyayı bir daha seçebilsin
    e.target.value = null;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        setIsExcelLoading(true);
        const bstr = evt.target.result;
        const workbook = XLSX.read(bstr, { type: 'binary' });
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (data.length === 0) {
          setExcelModal({ show: true, type: 'error', title: 'Hata', message: 'Excel dosyası boş veya okunamadı.' });
          setIsExcelLoading(false);
          return;
        }

        // Başlık kontrolü
        const firstRow = data[0];
        const isJaluzi = activeTypeTab === 'ahsapJaluzi';

        if (isJaluzi) {
          if (!('Ürün Adı' in firstRow) || !('Merdiven İpli' in firstRow)) {
            setExcelModal({ show: true, type: 'error', title: 'Hatalı Format', message: `Hatalı Excel formatı! Ahşap/Metal Jaluzi dosyalarında "Ürün Adı", "Merdiven İpli", "Kurdeleli" gibi başlıklar bulunmalıdır.\n\n(Lütfen taslağı görmek için önce "Excel'e Aktar" diyerek örnek dosyayı indirin)` });
            setIsExcelLoading(false);
            return;
          }
        } else {
          if (!('Ürün Adı' in firstRow) || !('Birim Fiyat' in firstRow)) {
            setExcelModal({ show: true, type: 'error', title: 'Hatalı Format', message: `Hatalı Excel formatı! Dosyada "Ürün Adı" ve "Birim Fiyat" başlıkları bulunmalıdır.\n\n(Lütfen taslağı görmek için önce "Excel'e Aktar" diyerek örnek dosyayı indirin)` });
            setIsExcelLoading(false);
            return;
          }
        }

        const hasStorEnTavanColumn = activeTypeTab === 'stor' && STOR_EN_TAVAN_COLUMN in firstRow;

        // Upsert onayı — ID'ler korunur, yalnızca güncelleme/ekleme yapılır
        const typeLabel = PRODUCT_TYPES[activeTypeTab]?.label;
        const existingCount = products.filter(p => p.productType === activeTypeTab).length;
        const storEnTavanNote = hasStorEnTavanColumn
          ? '\n• "En Tavanı" sütunu: "200 cm" veya "Serbest" (Evet/Hayır da kabul edilir)'
          : activeTypeTab === 'stor'
            ? '\n• İsteğe bağlı "En Tavanı" sütunu eklenebilir ("200 cm" / "Serbest")'
            : '';

        setExcelModal({
          show: true,
          type: 'confirm',
          title: 'Excel Yükleme Onayı',
          message: `Excel dosyasındaki ${data.length} ürün işlenecek:\n\n• Mevcut ${existingCount} ürün içinden eşleşenler güncellenir\n• Listede olmayan yeni ürünler eklenir\n• Listede olup Excel'de olmayan ürünlere dokunulmaz${storEnTavanNote}\n\nOnaylıyor musunuz?`,
          onConfirm: () => processExcelData(data, typeLabel, { hasStorEnTavanColumn })
        });

        // Modal açıkken arkada loading dönmesini kapatmak veya açık bırakmak size kalmış
        // Biz processExcelData içerisinde tekrar true/false yapacağız.
      } catch (error) {
        console.error('Excel dosyası parse edilirken hata:', error);
        setExcelModal({ show: true, type: 'error', title: 'Parse Hatası', message: 'Dosya okunurken bir hata oluştu.' });
        setIsExcelLoading(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  return (
    <div className="product-list-page">
      <div className="product-list-header">
        <div className="product-list-title">
          <h2>Ürün Listesi</h2>
          <p>Ürün tipine göre kategorize edilmiş ürün kataloğu</p>
        </div>

      </div>

      {/* Global Ayarlar (Döviz) - Topbar */}
      <div className="global-topbar">
        <div className="topbar-title">
          <i className="fas fa-coins" style={{ color: '#b04309' }}></i>
          <h4>Döviz Kur Ayarları</h4>
        </div>
        <div className="topbar-exchange-rates">
          <span className="topbar-rate" title="Güncel Dolar Kuru"><i className="fas fa-dollar-sign"></i> <b>{exchangeRates.USD}</b> ₺</span>
          <span className="topbar-rate" title="Güncel Euro Kuru"><i className="fas fa-euro-sign"></i> <b>{exchangeRates.EUR}</b> ₺</span>
        </div>
        <ExchangeRateSettings
          initialSettings={{ useManualRates, manualUsdRate, manualEurRate }}
          exchangeRates={exchangeRates}
          onSave={handleSaveSettings}
          readOnly={!canEditProducts}
        />
      </div>


      {/* Ürün Tipi Navigasyon */}
      <ProductCategoryNav
        activeTypeTab={activeTypeTab}
        handleTabChange={handleTabChange}
        products={products}
        isLoading={isLoading}
      />

      <div className="product-table-wrapper">
        <ProductTableHeader
          activeTypeTab={activeTypeTab}
          filteredProductsCount={filteredProducts.length}
          canEditProducts={canEditProducts}
          isExcelLoading={isExcelLoading}
          fileInputRef={fileInputRef}
          handleImportExcel={handleImportExcel}
          handleImportClick={handleImportClick}
          handleExportExcel={handleExportExcel}
          handleClearList={handleClearList}
        />

        <ProductTypeSettings
          activeTypeTab={activeTypeTab}
          canEditProducts={canEditProducts}
          handleSaveSettings={handleSaveSettings}
          isSavingSettings={isSavingSettings}
          rustikExtras={rustikExtras}
          setRustikExtras={setRustikExtras}
          rustikStates={{
            isRustikSettingsCollapsed, setIsRustikSettingsCollapsed,
            isRustikModelCollapsed, setIsRustikModelCollapsed,
            isRustikCapCollapsed, setIsRustikCapCollapsed,
            isRustikUzunlukCollapsed, setIsRustikUzunlukCollapsed,
            isRustikRenkCollapsed, setIsRustikRenkCollapsed,
            newRustikModel, setNewRustikModel,
            newRustikCap, setNewRustikCap,
            newRustikUzunluk, setNewRustikUzunluk,
            newRustikRenk, setNewRustikRenk,
            editingRustikModel, setEditingRustikModel,
            editingRustikCap, setEditingRustikCap,
            editingRustikUzunluk, setEditingRustikUzunluk,
            editingRustikRenk, setEditingRustikRenk
          }}
          tulExtraPrices={tulExtraPrices}
          setTulExtraPrices={setTulExtraPrices}
          kanatExtraPrices={kanatExtraPrices}
          setKanatExtraPrices={setKanatExtraPrices}
          jaluziExtras={jaluziExtras}
          setJaluziExtras={setJaluziExtras}
          metalJaluziExtras={metalJaluziExtras}
          setMetalJaluziExtras={setMetalJaluziExtras}
          storExtras={storExtras}
          setStorExtras={setStorExtras}
          zebraExtras={zebraExtras}
          setZebraExtras={setZebraExtras}
          dikeyExtras={dikeyExtras}
          setDikeyExtras={setDikeyExtras}
          dikeyPvcExtras={dikeyPvcExtras}
          setDikeyPvcExtras={setDikeyPvcExtras}
          dikeyStorExtras={dikeyStorExtras}
          setDikeyStorExtras={setDikeyStorExtras}
          katlamaliExtras={katlamaliExtras}
          setKatlamaliExtras={setKatlamaliExtras}
          pliseExtras={pliseExtras}
          setPliseExtras={setPliseExtras}
          katlamaliStates={{
            isKatlamaliMekanizmaCollapsed, setIsKatlamaliMekanizmaCollapsed,
            newKatlamaliMekanizma, setNewKatlamaliMekanizma
          }}
          dikeyStates={{
            isFonListCollapsed, setIsFonListCollapsed,
            newDikeyFonName, setNewDikeyFonName,
            editingDikeyFonName, setEditingDikeyFonName
          }}
        />

        <ProductSearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredProductsCount={filteredProducts.length}
          canEditProducts={canEditProducts}
          handleAddNew={handleAddNew}
        />

        {isLoading ? (
          <div className="product-loading">
            <i className="fas fa-spinner fa-spin"></i> Yükleniyor...
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="product-empty">
            <i className="fas fa-box-open"></i>
            <p>Bu kategoride henüz ürün yok.</p>
            {canEditProducts && (
              <button type="button" className="btn-add-product-empty" onClick={handleAddNew}>
                <span className="btn-add-product-empty-icon" aria-hidden="true">
                  <i className="fas fa-plus" />
                </span>
                İlk ürünü ekle
              </button>
            )}
          </div>
        ) : (
          <ProductDataTable
            activeTypeTab={activeTypeTab}
            filteredProducts={filteredProducts}
            canEditProducts={canEditProducts}
            handleEdit={handleEdit}
            setDeleteModal={setDeleteModal}
          />
        )}
      </div>

      {/* Ürün Ekleme/Düzenleme Formu Modal */}
      <ProductFormModal
        showForm={showForm}
        formData={formData}
        setFormData={setFormData}
        editingProduct={editingProduct}
        isSaving={isSaving}
        formError={formError}
        formSuccess={formSuccess}
        handleSave={handleSave}
        handleCancel={handleCancel}
      />

      {/* Silme Onay Modal */}
      <DeleteConfirmModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        handleDelete={handleDelete}
      />

      {/* Excel İşlemleri Modal */}
      <ExcelStatusModal
        excelModal={excelModal}
        setExcelModal={setExcelModal}
        setIsExcelLoading={setIsExcelLoading}
      />
    </div>
  );
};

export default ProductList;
